<div class="flex flex-col gap-8 animate-fadeIn">
  <!-- Header -->
  <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
    <div class="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:max-w-2xl">
      <div class="flex-1">
        <p-iconField iconPosition="left">
          <p-inputIcon styleClass="pi pi-search text-slate-400" />
          <input
            type="text"
            pInputText
            [formControl]="searchControl"
            [placeholder]="'speciality_search' | translate"
            class="w-full !bg-white !border-slate-200 !rounded-xl !text-sm focus:!ring-2 focus:!ring-primary/10 focus:!border-primary !shadow-sm transition-all" />
        </p-iconField>
      </div>

      <!-- Group filter -->
      <p-select
        [formControl]="groupFilterControl"
        [options]="groupFilterOptions"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        [placeholder]="'speciality_group_filter_placeholder' | translate"
        styleClass="w-full sm:w-60 !rounded-xl !border-slate-200 !shadow-sm !text-sm">
        <ng-template pTemplate="selectedItem" let-o>
          <span *ngIf="o" class="flex items-center gap-2 text-sm">
            <i class="pi text-xs" [ngClass]="o.isGroup ? 'pi-folder text-primary' : 'pi-filter text-slate-400'"></i>
            {{ o.label }}
          </span>
        </ng-template>
        <ng-template pTemplate="item" let-o>
          <span class="flex items-center gap-2 text-sm">
            <i class="pi text-xs" [ngClass]="o.isGroup ? 'pi-folder text-primary' : 'pi-filter text-slate-400'"></i>
            {{ o.label }}
          </span>
        </ng-template>
      </p-select>
    </div>

    <div class="flex items-center gap-2 shrink-0" *hasPermission="'Configurações:create'">
      <app-button
        label="{{ 'speciality_group_new' | translate }}"
        icon="pi pi-folder-plus"
        variant="secondary"
        (onClick)="onAddGroup()">
      </app-button>
      <app-button
        label="{{ 'speciality_new' | translate }}"
        icon="pi pi-plus"
        variant="primary"
        (onClick)="onAdd()">
      </app-button>
    </div>
  </div>

  <!-- Active group filter -->
  <div *ngIf="hasGroupFilter"
    class="flex items-center gap-2 flex-wrap -mt-4 px-3 py-2 rounded-xl bg-gradient-to-r from-blue-50 via-blue-50/30 to-transparent border border-slate-100">
    <i class="pi pi-filter-fill text-[10px] text-primary/40"></i>
    <span class="text-[9px] font-black uppercase tracking-[0.1em] text-slate-400">
      {{ 'speciality_group' | translate }}
    </span>
    <span class="text-xs font-bold text-slate-700">{{ activeGroupFilterLabel }}</span>
    <button type="button" (click)="clearGroupFilter()"
      class="ml-auto inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[9px] font-black uppercase tracking-[0.08em] text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all">
      <i class="pi pi-times-circle text-[10px]"></i>
      {{ 'speciality_group_filter_all' | translate }}
    </button>
  </div>

  <!-- Table -->
  <app-prime-table
    [data]="specialties"
    [columns]="columns"
    [loading]="loading"
    [templates]="templates"
    [paginator]="true"
    [rows]="rows"
    [totalRecords]="totalRecords"
    [lazy]="true"
    (onPage)="onPageChange($event)">
  </app-prime-table>

  <!-- Name Template: groups get a folder badge and member count -->
  <ng-template #nameTpl let-item>
    <div class="flex items-center gap-2 min-w-0">
      <span *ngIf="item.is_group"
        class="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary shrink-0">
        <i class="pi pi-folder text-xs"></i>
      </span>
      <div class="flex flex-col min-w-0">
        <span class="truncate" [ngClass]="item.is_group ? 'font-bold text-slate-800' : 'text-slate-700'">
          {{ item.name }}
        </span>
        <span *ngIf="item.is_group" class="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {{ 'speciality_group_badge' | translate }} ·
          {{ 'speciality_group_members' | translate: { count: memberCounts[item.id] || 0 } }}
        </span>
      </div>
    </div>
  </ng-template>

  <!-- Group Template: the group a speciality belongs to, clickable to filter -->
  <ng-template #groupTpl let-item>
    <ng-container *ngIf="!item.is_group">
      <button *ngIf="item.group; else noGroup" type="button" (click)="filterByGroup(item.group.id)"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-primary/10 hover:text-primary transition-colors">
        <i class="pi pi-folder text-[10px]"></i>
        {{ item.group.name }}
      </button>
      <ng-template #noGroup>
        <span class="text-xs text-slate-300">—</span>
      </ng-template>
    </ng-container>
  </ng-template>

  <!-- Status Template -->
  <ng-template #statusTpl let-item>
    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      [ngClass]="item.visibility ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
      {{ (item.visibility ? 'speciality_visible' : 'speciality_hidden') | translate }}
    </div>
  </ng-template>

  <!-- Actions Template -->
  <ng-template #actionsTpl let-item>
    <app-dropdown *ngIf="canEdit || canDelete">
      <ng-template #trigger>
        <button class="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all">
          <i class="pi pi-ellipsis-v"></i>
        </button>
      </ng-template>
      <ng-template #content>
        <div class="w-52 py-1">
          <!-- Edit -->
          <button *hasPermission="'Configurações:edit'" (click)="onEdit(item)"
            class="w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors">
            <i class="pi pi-pencil text-primary"></i>
            {{ (item.is_group ? 'speciality_group_edit' : 'speciality_edit') | translate }}
          </button>

          <!-- Add to / change group (specialities only) -->
          <ng-container *ngIf="!item.is_group">
            <button *hasPermission="'Configurações:edit'" (click)="openAssignModal(item)"
              class="w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors">
              <i class="pi pi-folder-open text-primary"></i>
              {{ (item.group_id ? 'speciality_group_change' : 'speciality_group_assign') | translate }}
            </button>
          </ng-container>

          <!-- Toggle Visibility -->
          <button *hasPermission="'Configurações:edit'" (click)="toggleVisibility(item)"
            class="w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 flex items-center gap-3 transition-colors">
            <i class="pi" [ngClass]="item.visibility ? 'pi-eye-slash text-amber-500' : 'pi-eye text-green-500'"></i>
            {{ (item.visibility ? 'speciality_hide' : 'speciality_show') | translate }}
          </button>

          <!-- Delete -->
          <button *hasPermission="'Configurações:delete'" (click)="openDeleteModal(item)"
            class="w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest text-rose-600 hover:bg-rose-50 flex items-center gap-3 transition-colors">
            <i class="pi pi-trash text-rose-500"></i>
            {{ 'delete' | translate }}
          </button>
        </div>
      </ng-template>
    </app-dropdown>
  </ng-template>
</div>

<!-- Delete Confirmation Modal -->
<p-dialog
  [(visible)]="showDeleteModal"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [showHeader]="false"
  [style]="{ width: '100%', maxWidth: '24rem' }"
  styleClass="p-0 overflow-hidden rounded-[2.5rem]">
    <div class="p-8 text-center bg-white">
      <div class="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-6 text-rose-500">
        <i class="pi pi-trash text-2xl"></i>
      </div>
      <h3 class="text-lg font-bold text-slate-800 mb-2">
        {{ (specialityToDelete?.is_group ? 'speciality_group_delete_title' : 'speciality_delete_title') | translate }}
      </h3>
      <p class="text-sm text-slate-500 mb-2">
        {{ (specialityToDelete?.is_group ? 'speciality_group_delete_confirm' : 'speciality_delete_confirm') | translate }}
        "{{ specialityToDelete?.name }}"?
      </p>
      <p *ngIf="specialityToDelete?.is_group" class="text-xs text-slate-400 mb-8">
        {{ 'speciality_group_delete_note' | translate }}
      </p>
      <div *ngIf="!specialityToDelete?.is_group" class="mb-6"></div>

      <div class="flex justify-end gap-3">
        <app-button
          [label]="'cancel' | translate"
          variant="secondary"
          (onClick)="showDeleteModal = false">
        </app-button>
        <app-button
          [label]="'delete' | translate"
          variant="primary"
          (onClick)="confirmDelete()"
          styleClass="!bg-rose-500 !hover:bg-rose-600 !border-rose-500">
        </app-button>
      </div>
    </div>
</p-dialog>

<!-- Add to Group Modal -->
<p-dialog
  [visible]="showAssignModal"
  (visibleChange)="closeAssignModal()"
  [modal]="true"
  [header]="'speciality_group_assign_title' | translate"
  [style]="{ width: '100%', maxWidth: '28rem' }"
  styleClass="p-0"
  [draggable]="false"
  [resizable]="false">
  <ng-template pTemplate="content">
    <div class="flex flex-col gap-4 pt-4">
      <p class="text-sm text-slate-500">
        <span class="font-semibold text-slate-700">{{ specialityToAssign?.name }}</span>
      </p>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-slate-700">{{ 'speciality_group' | translate }}</label>
        <p-select
          [(ngModel)]="assignGroupId"
          [options]="groups"
          optionLabel="name"
          optionValue="id"
          [showClear]="true"
          [filter]="groups.length > 6"
          appendTo="body"
          [placeholder]="'speciality_group_none' | translate"
          styleClass="w-full">
        </p-select>
        <small *ngIf="groups.length === 0" class="text-xs text-slate-400">
          {{ 'speciality_group_no_groups' | translate }}
        </small>
      </div>
    </div>
  </ng-template>
  <ng-template pTemplate="footer">
    <div class="flex items-center justify-end gap-2 pb-2">
      <app-button [label]="'common_cancel' | translate" variant="secondary" (onClick)="closeAssignModal()"></app-button>
      <app-button [label]="'common_save' | translate" [loading]="assigning" (onClick)="confirmAssign()"></app-button>
    </div>
  </ng-template>
</p-dialog>

<!-- Add/Edit Modal -->
<app-speciality-modal
  [visible]="showSpecialityModal"
  [speciality]="selectedSpeciality"
  [isGroup]="modalIsGroup"
  [groups]="groups"
  (visibleChange)="showSpecialityModal = $event"
  (saved)="onSaved()">
</app-speciality-modal>
